import React, { useState, useEffect, useCallback } from 'react';
import { formatCurrency } from '../../utils/invoiceHelpers';
import creditMemoService from '../../services/api/creditMemoService';

/**
 * ApplyCreditModal Component
 *
 * Modal for applying customer credit to an invoice.
 * Shows available credit memos and allows partial or full application.
 *
 * @param {boolean} isOpen - Whether modal is open
 * @param {function} onClose - Close handler
 * @param {object} invoice - Invoice to apply credit to
 * @param {number} customerId - Customer ID
 * @param {function} onApplySuccess - Success callback with application result
 */
const ApplyCreditModal = ({
  isOpen,
  onClose,
  invoice,
  customerId,
  onApplySuccess
}) => {
  const [availableCredits, setAvailableCredits] = useState([]);
  const [selectedCreditMemo, setSelectedCreditMemo] = useState(null);
  const [applyAmount, setApplyAmount] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');

  // Load available credits callback (defined before useEffect that uses it)
  const loadAvailableCredits = useCallback(async () => {
    try {
      setIsLoading(true);
      setError('');
      const result = await creditMemoService.getCustomerCredit(customerId);

      if (result.success) {
        // Filter to only show credits with remaining balance
        const creditsWithBalance = result.creditMemos?.filter(
          cm => cm.remainingBalanceCents > 0 && cm.status !== 'void'
        ) || [];
        setAvailableCredits(creditsWithBalance);
      }
    } catch (err) {
      setError('Failed to load available credits');
    } finally {
      setIsLoading(false);
    }
  }, [customerId]);

  // Load available credits when modal opens
  useEffect(() => {
    if (isOpen && customerId) {
      loadAvailableCredits();
    }
  }, [isOpen, customerId, loadAvailableCredits]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedCreditMemo(null);
      setApplyAmount('');
      setError('');
    }
  }, [isOpen]);

  // Set default amount when credit memo is selected
  useEffect(() => {
    if (selectedCreditMemo && invoice) {
      const invoiceBalance = (invoice.totalAmountCents - (invoice.amountPaidCents || 0)) / 100;
      const availableCredit = selectedCreditMemo.remainingBalanceCents / 100;
      const defaultAmount = Math.min(invoiceBalance, availableCredit);
      setApplyAmount(defaultAmount.toFixed(2));
    }
  }, [selectedCreditMemo, invoice]);

  const handleApplyCredit = async () => {
    if (!selectedCreditMemo) {
      setError('Please select a credit memo to apply');
      return;
    }

    const amountCents = Math.round(parseFloat(applyAmount) * 100);

    if (isNaN(amountCents) || amountCents <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    if (amountCents > selectedCreditMemo.remainingBalanceCents) {
      setError('Amount exceeds available credit');
      return;
    }

    const invoiceBalanceCents = invoice.totalAmountCents - (invoice.amountPaidCents || 0);
    if (amountCents > invoiceBalanceCents) {
      setError('Amount exceeds invoice balance');
      return;
    }

    try {
      setIsProcessing(true);
      setError('');

      const result = await creditMemoService.applyCreditToInvoice(
        selectedCreditMemo.id,
        invoice.id,
        amountCents
      );

      if (result.success) {
        onApplySuccess && onApplySuccess(result);
        onClose();
      } else {
        setError(result.error || 'Failed to apply credit');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to apply credit');
    } finally {
      setIsProcessing(false);
    }
  };

  const getTotalAvailableCredit = () => {
    return availableCredits.reduce((sum, cm) => sum + cm.remainingBalanceCents, 0);
  };

  const getInvoiceBalance = () => {
    if (!invoice) return 0;
    return invoice.totalAmountCents - (invoice.amountPaidCents || 0);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Apply Credit to Invoice</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            disabled={isProcessing}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Invoice Info */}
        {invoice && (
          <div className="mb-4 p-3 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600">Invoice</p>
            <p className="font-medium">{invoice.invoiceNumber}</p>
            <div className="flex justify-between mt-2 text-sm">
              <span className="text-gray-500">Balance Due:</span>
              <span className="font-medium">{formatCurrency(getInvoiceBalance() / 100)}</span>
            </div>
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        )}

        {/* No Credits Available */}
        {!isLoading && availableCredits.length === 0 && (
          <div className="text-center py-6">
            <p className="text-gray-500">No available credits for this customer.</p>
            <button
              onClick={onClose}
              className="mt-4 px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
            >
              Close
            </button>
          </div>
        )}

        {/* Credits List */}
        {!isLoading && availableCredits.length > 0 && (
          <>
            {/* Total Available */}
            <div className="mb-4 p-3 bg-green-50 rounded-lg border border-green-200">
              <div className="flex justify-between items-center">
                <span className="text-sm text-green-700">Total Available Credit:</span>
                <span className="text-lg font-bold text-green-700">
                  {formatCurrency(getTotalAvailableCredit() / 100)}
                </span>
              </div>
            </div>

            {/* Credit Memo Selection */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Credit Memo
              </label>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {availableCredits.map((credit) => (
                  <div
                    key={credit.id}
                    className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                      selectedCreditMemo?.id === credit.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => setSelectedCreditMemo(credit)}
                  >
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="font-medium text-sm">{credit.creditMemoNumber}</p>
                        <p className="text-xs text-gray-500">{credit.reason}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-medium text-green-600">
                          {formatCurrency(credit.remainingBalanceCents / 100)}
                        </p>
                        <p className="text-xs text-gray-400">available</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Amount Input */}
            {selectedCreditMemo && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Amount to Apply
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-2 text-gray-500">$</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    max={Math.min(
                      selectedCreditMemo.remainingBalanceCents / 100,
                      getInvoiceBalance() / 100
                    )}
                    value={applyAmount}
                    onChange={(e) => setApplyAmount(e.target.value)}
                    className="w-full pl-8 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="0.00"
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Max: {formatCurrency(Math.min(
                    selectedCreditMemo.remainingBalanceCents / 100,
                    getInvoiceBalance() / 100
                  ))}
                </p>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end space-x-3">
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                disabled={isProcessing}
              >
                Cancel
              </button>
              <button
                onClick={handleApplyCredit}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                disabled={!selectedCreditMemo || !applyAmount || isProcessing}
              >
                {isProcessing ? 'Applying...' : 'Apply Credit'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ApplyCreditModal;
